import { TYPE } from "@gaoya/shared";
import { displayConnHint, isMyTurn, type UiState } from "../game/state.js";
import type { AssetBank } from "./assets.js";
import { C, drawButton, fillRound, hash, isPressed, lattice, paperBg, wrapLines } from "./theme.js";
import { candidateButtons } from "../ui/keyboard.js";
import {
  layoutBattleHud,
  layoutHall,
  layoutLeaderboard,
  layoutLogin,
  layoutMatching,
  layoutPrivacy,
  layoutPrivate,
  layoutResult,
  layoutSettings,
  type UiButton,
} from "../ui/hall.js";
import { battleBands, drawBattle } from "./screens/battle.js";
import { drawHall } from "./screens/hall.js";
import { drawLeaderboard } from "./screens/leaderboard.js";
import { drawLogin } from "./screens/login.js";
import { drawMatching } from "./screens/matching.js";
import { drawPrivate } from "./screens/private.js";
import { drawResult } from "./screens/result.js";
import { drawPrivacy } from "./screens/privacy.js";
import { drawSettings } from "./screens/settings.js";

export { battleBands } from "./screens/battle.js";
export type { BattleBands } from "./screens/battle.js";

export function collectButtons(ui: UiState, w: number, h: number): UiButton[] {
  if (ui.showPrivacy) return layoutPrivacy(w, h);
  if (ui.showSettings) return layoutSettings(w, h);
  if (ui.showLeaderboard) return layoutLeaderboard(w, h, ui);
  switch (ui.screen) {
    case "login":
    case "boot":
      return layoutLogin(w, h, ui);
    case "hall":
      return layoutHall(w, h, ui);
    case "matching":
      return layoutMatching(w, h, ui);
    case "private":
      return layoutPrivate(w, h, ui);
    case "result":
      return layoutResult(w, h, ui);
    case "battle": {
      const bands = battleBands(w, h, ui);
      const hud = layoutBattleHud(w, bands.layout.opponentBar, bands.ident, ui);
      const cands = isMyTurn(ui) ? candidateButtons(bands.cand, ui.candidates, ui.hintWord) : [];
      return [...hud, ...cands];
    }
    default:
      return [];
  }
}

export function drawFrame(
  ctx: CanvasRenderingContext2D,
  ui: UiState,
  assets: AssetBank,
  w: number,
  h: number,
  now: number,
): void {
  ctx.save();
  if (now < ui.shakeUntil) {
    ctx.translate((hash(now, 1) - 0.5) * 7, (hash(now, 2) - 0.5) * 5);
  }
  paperBg(ctx, w, h, assets);
  lattice(ctx, w, h, ui.capsuleLeft);

  switch (ui.screen) {
    case "boot":
    case "login":
      drawLogin(ctx, ui, w, h);
      break;
    case "hall":
      drawHall(ctx, ui, w, h);
      break;
    case "matching":
      drawMatching(ctx, ui, w, h, now);
      break;
    case "private":
      drawPrivate(ctx, ui, w, h, now);
      break;
    case "battle":
      drawBattle(ctx, ui, w, h, now);
      break;
    case "result":
      drawResult(ctx, ui, w, h);
      break;
    default:
      break;
  }

  if (ui.showLeaderboard) drawLeaderboard(ctx, ui, w, h);
  if (ui.showSettings) drawSettings(ctx, ui, w, h);
  if (ui.showPrivacy) drawPrivacy(ctx, ui, w, h);

  const buttons = collectButtons(ui, w, h);
  for (const b of buttons) drawButton(ctx, b, isPressed(b, ui.pointerDown, ui.pointerX, ui.pointerY));

  drawToast(ctx, ui, w, h, now);
  drawConn(ctx, ui, w);
  ctx.restore();
}

function drawToast(ctx: CanvasRenderingContext2D, ui: UiState, w: number, h: number, now: number): void {
  if (!ui.toast || now > ui.toastUntil) return;
  ctx.font = `500 14px ${TYPE.sans}`;
  const padL = 32;
  const padR = 16;
  const innerMax = Math.max(80, w - 24 - padL - padR);
  const lines = wrapLines(ctx, ui.toast, innerMax);
  let textW = 0;
  for (const line of lines) textW = Math.max(textW, ctx.measureText(line).width);
  const width = Math.min(w - 24, Math.max(160, textW + padL + padR));
  const lineH = 20;
  const height = Math.max(40, 12 + lines.length * lineH);
  const x = (w - width) / 2;
  const y = ui.screen === "battle" ? Math.max(ui.safeTop + 48, h * 0.34) : Math.max(96, h * 0.13);
  fillRound(ctx, x, y, width, height, Math.min(20, height / 2), "rgba(42,33,27,0.92)");
  fillRound(ctx, x + 14, y + height / 2 - 3, 6, 6, 3, C.gold);
  ctx.fillStyle = "#FFF7EE";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const tx = x + padL;
  const ty0 = y + height / 2 - ((lines.length - 1) * lineH) / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i] ?? "", tx, ty0 + i * lineH);
  }
}

function drawConn(ctx: CanvasRenderingContext2D, ui: UiState, w: number): void {
  if (ui.connected || ui.screen === "login" || ui.screen === "boot") return;
  const text = displayConnHint(ui);
  ctx.font = `600 11px ${TYPE.sans}`;
  const tw = ctx.measureText(text).width + 26;
  const x = (w - tw) / 2;
  const y = Math.max(4, ui.safeTop + 2);
  fillRound(ctx, x, y, tw, 20, 10, C.sealSoft);
  fillRound(ctx, x + 8, y + 7, 6, 6, 3, C.seal);
  ctx.fillStyle = C.sealDeep;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + 18, y + 10.5);
}
